import { debounce } from 'lodash'
import { observer } from 'mobx-react-lite'
import { getSnapshot } from 'mobx-state-tree'
import React, { useCallback, useMemo } from 'react'
import { ActivityIndicator, ImageBackground, StyleSheet, TextInput, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { ScrollView, TouchableOpacity } from 'react-native-gesture-handler'
import { List, Modal, RadioButton } from 'react-native-paper'

import Clipboard from '@react-native-clipboard/clipboard'

import image from '../../../assets/Admin/pokerpower-watermark.png'
import { Text } from '../../components'
import { translate } from '../../i18n'
import { EnvironmentStages, useStore } from '../../models'
import { useQuery } from '../../models/api-store'
import { useMutation } from '@tanstack/react-query'
import { useAction } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import { reloadAppAsync } from 'expo'
import { Dropdown } from 'react-native-element-dropdown'
import { useRouter } from 'expo-router'
import { useActiveConvexDeployment } from '@/providers/ConvexProvider'
import { AntDesign, MaterialCommunityIcons } from '@expo/vector-icons'
import { DS } from '@/design-system'
import { Pressable } from 'react-native'
import { useConfig } from '../../hooks/useConfig'
import * as Updates from 'expo-updates'
import { getDetailedDeviceInfo } from '../../models/analytics/segment/segment'

const startStyles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column'
  },
  image: {
    flex: 1,
    resizeMode: 'cover',
    justifyContent: 'center'
  },
  text: {
    color: 'white',
    fontSize: 42,
    fontWeight: 'bold',
    textAlign: 'center',
    backgroundColor: '#000000a0'
  }
})

export const AdminEnvironmentScreen = observer(() => {
  // const navigation = useNavigation<DrawerNavigation>()
  const router = useRouter()
  const store = useStore()
  const { baseURL, baseWebSocketURL } = store.environment.getEnv()
  const stage = store.environment.stage

  const [message, setMessage] = React.useState('')
  const [visible, setVisible] = React.useState(false)
  //@ts-ignore null is not assignable to EnvironmentStages
  const [checked, setCheckedState] = React.useState<EnvironmentStages>(stage)

  const showModal = () => setVisible(true)
  const hideModal = () => setVisible(false)
  const containerStyle = { backgroundColor: 'white', padding: 20 }

  const setChecked = async (value: EnvironmentStages) => {
    try {
      setCheckedState(value)
      if (value === checked) {
        return
      }

      await store.environment.setStage(value)
      store.session.logout()
      // Immediately reload the React Native Bundle
      await reloadAppAsync()
    } catch (error) {
      console.warn('restart', error)
    }
  }

  return (
    <ImageBackground source={image} style={{ flex: 1 }}>
      <SafeAreaView style={{ flexGrow: 1 }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: 16 }}>
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'center' }}>
            <MaterialCommunityIcons name="help-network" size={24} color="black" />
            <DS.Text variant="Heading 3/Regular">Environment</DS.Text>
          </View>
          <Pressable onPress={() => router.back()} hitSlop={10}>
            <AntDesign name="close" size={24} color="black" />
          </Pressable>
        </View>

        <View style={{ backgroundColor: 'rgba(255,255,255,0.45)', flex: 1 }}>
          <ScrollView contentContainerStyle={{ paddingBottom: 32 }}>
            <List.Item
              style={{ margin: 5 }}
              title={translate('adminEnvironmentScreen.envTitle', { env: stage })}
              description="Tap radio button below to switch environment"
              // description={translate('adminEnvironmentScreen.envSubtitle', {
              //   fromEnv: isSwitchOn ? 'prod' : 'dev',
              //   toEnv: isSwitchOn ? 'dev' : 'prod',
              // })}
            />

            <View style={{ paddingHorizontal: 24 }}>
              <View style={{ gap: 8 }}>
                <TouchableOpacity
                  onPress={() => setChecked(EnvironmentStages.local)}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    borderWidth: 1,
                    paddingHorizontal: 12,
                    borderRadius: 12,
                    borderColor: '#DDDAD2'
                  }}
                >
                  <Text allowFontScaling={false} style={{ flex: 0.25, color: 'black' }}>
                    {EnvironmentStages.local}
                  </Text>
                  <RadioButton
                    value={EnvironmentStages.local}
                    status={checked === EnvironmentStages.local ? 'checked' : 'unchecked'}
                    onPress={() => setChecked(EnvironmentStages.local)}
                    uncheckedColor="grey"
                  />
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => setChecked(EnvironmentStages.dev)}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    borderWidth: 1,
                    paddingHorizontal: 12,
                    borderRadius: 12,
                    borderColor: '#DDDAD2'
                  }}
                >
                  <Text allowFontScaling={false} style={{ flex: 0.25, color: 'black' }}>
                    {EnvironmentStages.dev}
                  </Text>
                  <RadioButton
                    value={EnvironmentStages.dev}
                    status={checked === EnvironmentStages.dev ? 'checked' : 'unchecked'}
                    uncheckedColor="grey"
                  />
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => setChecked(EnvironmentStages.prod)}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    borderWidth: 1,
                    paddingHorizontal: 12,
                    borderRadius: 12,
                    borderColor: '#DDDAD2'
                  }}
                >
                  <Text allowFontScaling={false} style={{ flex: 0.25, color: 'black' }}>
                    {EnvironmentStages.prod}
                  </Text>
                  <RadioButton
                    value={EnvironmentStages.prod}
                    status={checked === EnvironmentStages.prod ? 'checked' : 'unchecked'}
                    onPress={() => setChecked(EnvironmentStages.prod)}
                    uncheckedColor="grey"
                  />
                </TouchableOpacity>
              </View>
            </View>
            <PathMappingInput />
            <ConvexDeploymentInput />
            <List.Item
              onPress={() => {
                Clipboard.setString(baseURL)
              }}
              style={{ margin: 5 }}
              title="Base URL"
              description={baseURL}
            />
            <List.Item
              onPress={() => {
                Clipboard.setString(baseWebSocketURL)
              }}
              style={{ margin: 5 }}
              title="Base WebSocket URL"
              description={baseWebSocketURL}
            />
            <List.Item
              // icon="content-paste"
              onPress={debounce(() => {
                const snapshot = getSnapshot(store)
                // NOTE: removing the GraphGL query cache
                // if (snapshot['api']) {
                //   delete snapshot['api']
                // }
                const pretty = JSON.stringify(snapshot, null, 2)
                setMessage(pretty)
                Clipboard.setString(pretty)
                showModal()
              }, 250)}
              title={translate('adminEnvironmentScreen.appStateSnapshot')}
              description="Tap to copy snapshot to clipboard"
            />
            <View style={{ paddingHorizontal: 16 }}>
              <DS.Text variant="Body Medium/Regular">Device Details</DS.Text>
              <DS.Text variant="Body Small/Regular">
                {getDetailedDeviceInfo ? JSON.stringify(getDetailedDeviceInfo(), null, 2) : 'No device'}
              </DS.Text>
            </View>
          </ScrollView>
          <Modal visible={visible} onDismiss={hideModal} contentContainerStyle={containerStyle}>
            <Text
              allowFontScaling={false}
              tx="adminMessageScreen.howToDismissMessage"
              style={{ color: 'black', textAlign: 'center' }}
            />
            <Text allowFontScaling={false}>{'\n'}</Text>
            <ScrollView>
              <Text allowFontScaling={false} style={{ color: 'black' }}>
                {message}
              </Text>
            </ScrollView>
          </Modal>
        </View>
      </SafeAreaView>
    </ImageBackground>
  )
})

const PathMappingInput = observer(() => {
  const mappings = useQuery((root) => root.queryGetApiGatewayMappings())
  const store = useStore()
  const [customPrefix, setCustomPrefix] = React.useState('')

  const data = useMemo(
    () =>
      mappings.data?.getApiGatewayMappings.map((mapping) => ({
        label: mapping === '' ? 'Default' : mapping,
        value: mapping
      })) ?? [],
    [mappings.data?.getApiGatewayMappings]
  )

  const selectMutation = useMutation({
    mutationFn: async (path: string) => {
      await store.environment.setPathPrefix(path)
      await reloadAppAsync()
    }
  })

  const onSelect = (value: string) => {
    selectMutation.mutate(value)
  }

  const value = store.environment.pathPrefix

  const canApplyCustom = customPrefix.trim() !== ''

  const handleApplyCustomPrefix = () => {
    const valueToApply = customPrefix.trim()
    if (valueToApply !== '') {
      selectMutation.mutate(valueToApply)
    }
  }

  if (mappings.loading || selectMutation.isPending) {
    return <ActivityIndicator />
  }

  return (
    <>
      <List.Item
        onPress={() => {
          Clipboard.setString(value)
        }}
        style={{ margin: 5 }}
        title="API Prefix"
      />
      <Dropdown
        style={dropDownStyles.dropdown}
        placeholderStyle={dropDownStyles.placeholderStyle}
        selectedTextStyle={dropDownStyles.selectedTextStyle}
        inputSearchStyle={dropDownStyles.inputSearchStyle}
        iconStyle={dropDownStyles.iconStyle}
        data={data}
        search
        maxHeight={300}
        labelField="label"
        valueField="value"
        placeholder="Select item"
        searchPlaceholder="Search..."
        value={value}
        onChange={(item) => {
          onSelect(item.value)
        }}
      />
      <View style={dropDownStyles.customPrefixContainer}>
        <TextInput
          style={dropDownStyles.customPrefixInput}
          placeholder="Or enter custom API prefix"
          placeholderTextColor="#999"
          value={customPrefix}
          onChangeText={setCustomPrefix}
          autoCapitalize="none"
          autoCorrect={false}
        />
        <TouchableOpacity
          style={[dropDownStyles.applyButton, !canApplyCustom && dropDownStyles.applyButtonDisabled]}
          onPress={handleApplyCustomPrefix}
          disabled={!canApplyCustom || selectMutation.isPending}
        >
          <Text allowFontScaling={false} style={{ color: 'white', fontSize: 16, fontWeight: '500' }}>
            Use custom
          </Text>
        </TouchableOpacity>
      </View>
    </>
  )
})

type ConvexDeploymentOption = {
  label: string
  value: string
  project?: string
  isHeader?: boolean
}

const STATIC_CONVEX_DEPLOYMENTS: ConvexDeploymentOption[] = [
  {
    label: 'Default (build-time, in app, clear memory)',
    value: ''
  },
  {
    label: 'Local Host',
    value: 'http://localhost:3210'
  }
]

const getConvexUrlFromDeployment = (deployment: string) => {
  if (!deployment) {
    return process.env.EXPO_PUBLIC_CONVEX_URL ?? 'https://chatty-sandpiper-749.convex.cloud'
  }

  if (deployment.startsWith('http://') || deployment.startsWith('https://')) {
    return deployment
  }

  return `https://${deployment}.convex.cloud`
}

const ConvexDeploymentInput = observer(() => {
  const {
    extra: { channel } = {}
  } = useConfig()
  const store = useStore()
  const currentDeployment = store.environment.convexDeployment
  const { convexUrl: activeUrl, source } = useActiveConvexDeployment()
  const currentChannel = `config: ${channel ?? 'missing'} eas: ${Updates?.channel ?? 'missing'}`

  const fetchDeployments = useAction(api.admin.actions.getDeployments)
  const [fetched, setFetched] = React.useState<ConvexDeploymentOption[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  const userId = store.session.user?.auth0Id ?? 'unknown'

  const loadDeployments = useCallback(() => {
    setLoading(true)
    setError(null)
    fetchDeployments({ userId })
      .then((result) => setFetched(result as ConvexDeploymentOption[]))
      .catch((err) => {
        console.error('[getDeployments]', err)
        setError(err instanceof Error ? err.message : String(err))
      })
      .finally(() => setLoading(false))
  }, [fetchDeployments, userId])

  React.useEffect(() => {
    loadDeployments()
  }, [loadDeployments])

  const deployments = useMemo<ConvexDeploymentOption[]>(() => {
    const byProject = fetched.reduce<Record<string, ConvexDeploymentOption[]>>((acc, d) => {
      const key = d.project ?? 'Other'
      // biome-ignore lint: complex but ok as is
      ;(acc[key] ??= []).push(d)
      return acc
    }, {})
    const grouped = Object.entries(byProject).flatMap(([project, items]) => [
      { label: project, value: `__header__${project}`, isHeader: true },
      ...items
    ])
    return [...STATIC_CONVEX_DEPLOYMENTS, ...grouped]
  }, [fetched])

  const selectMutation = useMutation({
    mutationFn: async (deployment: string) => {
      await store.environment.setConvexDeployment(deployment)
      await reloadAppAsync()
    }
  })

  const onSelect = (value: string) => {
    selectMutation.mutate(value)
  }

  if (selectMutation.isPending) {
    return <ActivityIndicator />
  }

  return (
    <>
      <List.Item
        onPress={() => {
          Clipboard.setString(activeUrl)
        }}
        style={{ margin: 5 }}
        title={`Convex deployment (source: ${source})`}
        description={activeUrl}
      />
      <Dropdown
        style={dropDownStyles.dropdown}
        placeholderStyle={dropDownStyles.placeholderStyle}
        selectedTextStyle={dropDownStyles.selectedTextStyle}
        inputSearchStyle={dropDownStyles.inputSearchStyle}
        iconStyle={dropDownStyles.iconStyle}
        data={deployments}
        search
        maxHeight={300}
        labelField="label"
        valueField="value"
        placeholder={loading ? 'Loading deployments...' : 'Select deployment'}
        searchPlaceholder="Search deployments..."
        value={currentDeployment}
        onChange={(item) => {
          if (!item.isHeader) onSelect(item.value)
        }}
        renderItem={(item) => {
          if (item.isHeader) {
            return (
              <View style={{ paddingHorizontal: 16, paddingVertical: 6, backgroundColor: '#f0ede8' }}>
                <Text
                  allowFontScaling={false}
                  style={{ color: '#666', fontSize: 12, fontWeight: '600', textTransform: 'uppercase' }}
                >
                  {item.label}
                </Text>
              </View>
            )
          }
          return (
            <View style={{ paddingHorizontal: 16, paddingVertical: 12 }}>
              <Text allowFontScaling={false} style={{ color: '#333', fontSize: 14 }}>
                {item.label}
              </Text>
            </View>
          )
        }}
      />
      {error ? (
        <View style={dropDownStyles.statusContainer}>
          <Text allowFontScaling={false} style={dropDownStyles.errorText}>
            Failed to load deployments: {error}
          </Text>
          <TouchableOpacity style={dropDownStyles.retryButton} onPress={loadDeployments}>
            <Text allowFontScaling={false} style={{ color: 'white', fontSize: 14, fontWeight: '500' }}>
              Retry
            </Text>
          </TouchableOpacity>
        </View>
      ) : !loading && fetched.length === 0 ? (
        <View style={dropDownStyles.statusContainer}>
          <Text allowFontScaling={false} style={dropDownStyles.hintText}>
            No remote deployments returned. This Convex deployment may be running stale code or missing
            CONVEX_MANAGEMENT_TOKEN, so only the static options above are available.
          </Text>
          <TouchableOpacity style={dropDownStyles.retryButton} onPress={loadDeployments}>
            <Text allowFontScaling={false} style={{ color: 'white', fontSize: 14, fontWeight: '500' }}>
              Retry
            </Text>
          </TouchableOpacity>
        </View>
      ) : null}
      <List.Item
        onPress={() => {
          Clipboard.setString(currentChannel)
        }}
        style={{ margin: 2 }}
        title="Expo Channel"
        description={currentChannel}
      />
      <List.Item
        onPress={() => {
          Clipboard.setString(currentChannel)
        }}
        style={{ margin: 2 }}
        title="Test OTA Update"
        description={'Mar 27 - Latest Daily10 onto `development`'}
      />
    </>
  )
})

const dropDownStyles = StyleSheet.create({
  dropdown: {
    marginHorizontal: 16,
    height: 50,
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 12,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1
    },
    shadowOpacity: 0.2,
    shadowRadius: 1.41,
    elevation: 2
  },

  placeholderStyle: {
    fontSize: 16
  },
  selectedTextStyle: {
    fontSize: 16
  },
  iconStyle: {
    width: 20,
    height: 20
  },
  inputSearchStyle: {
    height: 40,
    fontSize: 16
  },
  customPrefixContainer: {
    marginHorizontal: 16,
    marginTop: 12,
    gap: 8
  },
  customPrefixInput: {
    height: 50,
    backgroundColor: 'white',
    borderRadius: 12,
    paddingHorizontal: 12,
    fontSize: 16,
    borderWidth: 1,
    borderColor: '#DDDAD2'
  },
  applyButton: {
    height: 44,
    backgroundColor: DS.palette['Brand/Purple'],
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center'
  },
  applyButtonDisabled: {
    opacity: 0.5
  },
  statusContainer: {
    marginHorizontal: 16,
    marginTop: 8,
    gap: 8
  },
  errorText: {
    color: '#B00020',
    fontSize: 13
  },
  hintText: {
    color: '#8a6d3b',
    fontSize: 13
  },
  retryButton: {
    alignSelf: 'flex-start',
    height: 40,
    paddingHorizontal: 16,
    backgroundColor: DS.palette['Brand/Purple'],
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center'
  }
})
